import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListTodo, ArrowRight, CheckCircle2, Circle, Calendar, Bell, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import ParkLotSweepModal from './ParkLotSweepModal';

/**
 * Dashboard "Park Lot" widget (iter-75).
 *
 * Surfaces the top 5 PENDING roadmap items across ALL of the user's active
 * goals — a single glanceable surface for "things I want to do eventually"
 * that lives outside the AI mission engine. Tapping an item jumps to its
 * parent goal so the user can manage it inline.
 */
export default function RoadmapParkLot() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sweepOpen, setSweepOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/users/me/roadmap-summary?limit=5');
      setItems(data.items || []);
      setTotalPending(data.total_pending || 0);
    } catch {
      // silent — widget is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh when items are added/toggled elsewhere on the same page session.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('roadmap:refresh', handler);
    return () => window.removeEventListener('roadmap:refresh', handler);
  }, [load]);

  const toggle = async (item, e) => {
    e?.stopPropagation();
    const next = item.status === 'done' ? 'pending' : 'done';
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    setTotalPending((c) => Math.max(0, c - 1));
    try {
      await api.patch(`/goals/${item.goal_id}/roadmap/${item.id}`, { status: next });
      toast.success(next === 'done' ? 'Nice — marked done' : 'Back to pending');
    } catch {
      toast.error('Could not update');
      load();
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section className="mt-5" data-testid="dashboard-park-lot">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase font-extrabold text-[#7E5BEF] tracking-wider flex items-center gap-1">
          <ListTodo size={14} strokeWidth={3} /> Park Lot
          <span className="text-[10px] font-extrabold text-[#94A3B8] ml-1">
            ({totalPending} pending across your goals)
          </span>
        </p>
        <button
          onClick={() => setSweepOpen(true)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#7E5BEF] to-[#1CB0F6] text-white text-[10px] font-extrabold uppercase tracking-wider shadow-sm"
          data-testid="park-lot-sweep-open"
        >
          <Sparkles size={10} strokeWidth={3} /> AI Sweep
        </button>
      </div>

      <div className="gm-card p-3 space-y-1.5">
        <ul className="space-y-1.5">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-start gap-2 p-2 rounded-xl border border-[#E5E7EB] dark:border-[#334155] bg-white dark:bg-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#0F172A] cursor-pointer transition-colors"
              onClick={() => navigate(`/goals/${item.goal_id}`)}
              data-testid={`park-lot-item-${item.id}`}
            >
              <button
                onClick={(e) => toggle(item, e)}
                className="mt-0.5 flex-shrink-0"
                aria-label="Mark done"
                data-testid={`park-lot-toggle-${item.id}`}
              >
                {item.status === 'done'
                  ? <CheckCircle2 size={18} strokeWidth={3} color="#16A34A" />
                  : <Circle size={18} strokeWidth={3} color="#94A3B8" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold leading-tight text-[#0F172A] dark:text-white truncate">
                  {item.title}
                </p>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] font-extrabold text-[#94A3B8]">
                  <span className="truncate max-w-[120px] text-[#7E5BEF]">{item.goal_title}</span>
                  {item.due_at && (
                    <span className="inline-flex items-center gap-0.5">
                      <Calendar size={9} strokeWidth={3} /> {String(item.due_at).slice(0, 10)}
                    </span>
                  )}
                  {item.reminder_at && (
                    <span className="inline-flex items-center gap-0.5 text-[#FBBF24]">
                      <Bell size={9} strokeWidth={3} />
                    </span>
                  )}
                </div>
              </div>
              <ArrowRight size={14} strokeWidth={3} className="mt-1 text-[#94A3B8] flex-shrink-0" />
            </li>
          ))}
        </ul>
        {totalPending > items.length && (
          <p className="text-[10px] font-extrabold text-[#94A3B8] text-center pt-1">
            + {totalPending - items.length} more — open each goal to see them all
          </p>
        )}
      </div>
      <ParkLotSweepModal open={sweepOpen} onClose={() => setSweepOpen(false)} />
    </section>
  );
}
